import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

/**
 * Competition Logo Upload, Storage Management & Consistent Presentation SDD
 * (`docs/incoming/sdd-competition-logo-upload-storage-and-presentation-logo.md`
 * §7/§8/§16, DATA-050/API-094..097). Mirrors the `enrollment_proofs` /
 * `eligibility.ts` upload-intent/finalize pattern — an asset-metadata row per
 * uploaded object, never the image binary itself, in D1.
 */

// ---------------------------------------------------------------------------
// Enums / constants
// ---------------------------------------------------------------------------

/** §7.1 — SVG/GIF/BMP/TIFF and anything else are rejected in this phase. */
export const COMPETITION_LOGO_ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'] as const
export const COMPETITION_LOGO_ALLOWED_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const

/** §7.2. */
export const COMPETITION_LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024
/** §7.3. Advisory on the client (§7.3 "第一版不強制"); not re-enforced server-side — see the finalize handler's own note. */
export const COMPETITION_LOGO_MIN_DIMENSION_PX = 256

export const COMPETITION_LOGO_ASSET_STATUS_VALUES = ['uploaded', 'ready', 'orphaned'] as const
export const competitionLogoAssetStatusSchema = z.enum(COMPETITION_LOGO_ASSET_STATUS_VALUES)
export type CompetitionLogoAssetStatus = z.infer<typeof competitionLogoAssetStatusSchema>

// ---------------------------------------------------------------------------
// Domain — CompetitionLogoAsset (owns the public R2 object; never the image binary in D1)
// ---------------------------------------------------------------------------

export const competitionLogoAssetSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  r2Key: z.string().min(1),
  contentType: z.enum(COMPETITION_LOGO_ALLOWED_CONTENT_TYPES),
  extension: z.string().min(1).max(16),
  sizeBytes: z.int().min(1).max(COMPETITION_LOGO_MAX_SIZE_BYTES),
  width: z.int().min(1).nullable(),
  height: z.int().min(1).nullable(),
  status: competitionLogoAssetStatusSchema,
  uploadExpiresAt: utcTimestampSchema.nullable(),
  createdAt: utcTimestampSchema,
  createdByUserId: publicIdSchema,
})
export type CompetitionLogoAsset = z.infer<typeof competitionLogoAssetSchema>

// ---------------------------------------------------------------------------
// Presentation — the read-only object every DTO embeds as `logo` (§16)
// ---------------------------------------------------------------------------

export const competitionLogoDtoSchema = z.object({
  assetId: publicIdSchema,
  url: z.url(),
  mimeType: z.enum(COMPETITION_LOGO_ALLOWED_CONTENT_TYPES),
  width: z.int().nullable(),
  height: z.int().nullable(),
})
export type CompetitionLogoDto = z.infer<typeof competitionLogoDtoSchema>

// ---------------------------------------------------------------------------
// API-094 — Upload Logo (intent / finalize)
// ---------------------------------------------------------------------------

export const competitionLogoUploadIntentRequestSchema = z.strictObject({
  filename: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(255),
  sizeBytes: z.int().min(1),
})
export type CompetitionLogoUploadIntentRequest = z.infer<
  typeof competitionLogoUploadIntentRequestSchema
>

export const competitionLogoUploadFinalizeRequestSchema = z.strictObject({
  etag: z.string().min(1).nullable().default(null),
  /**
   * Client-observed image dimensions (via `Image.decode()`/`naturalWidth`
   * before upload). Stored as a display hint only — never trusted as a
   * validated fact, since a client can report anything; §7.3 explicitly does
   * not require server-side dimension enforcement in this phase.
   */
  width: z.int().min(1).max(20_000).nullable().default(null),
  height: z.int().min(1).max(20_000).nullable().default(null),
})
export type CompetitionLogoUploadFinalizeRequest = z.infer<
  typeof competitionLogoUploadFinalizeRequestSchema
>
